import React, { ReactNode } from 'react';
import i18n from 'i18next';
import Helmet from 'react-helmet';
import { isEditorActive, withSitecoreContext, LayoutServiceData, SitecoreContextValue } from '@sitecore-jss/sitecore-jss-react';
import { layoutServiceFactory } from './lib/layout-service-factory';
import config from './temp/config';
import LayoutFactory from './LayoutFactory';

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.

export interface RouteHandlerProps {
  sitecoreContext: SitecoreContextValue;
  route: any; // Route<CustomRouteProps>;
  children?: ReactNode;
  isSSR: boolean;
  language?: string;
  url: string;
  updateSitecoreContext: (value: SitecoreContextValue) => void;
}

type RouteHandlerState = {
  notFound: boolean;
  loading: boolean;
};

class RouteHandler extends React.Component<RouteHandlerProps, RouteHandlerState> {

  public state: RouteHandlerState = {
    notFound: false,
    loading: false
  };

  constructor(props) {
    super(props);

    const routeData = this.extractRouteData();

    // route data from react-router - if route was resolved, it's not a 404
    if (routeData !== null) {
      this.state.notFound = false;
    }

    // if we have an initial SSR state, and that state doesn't have a valid route data,
    // then this is a 404 route.
    if (this.props.isSSR && routeData && (!routeData.sitecore || !routeData.sitecore.route)) {
      this.state.notFound = true;
    }

    // tell i18next to sync its current language with the route language
    this.updateLanguage();
  }

  componentDidMount() {
    // If we are not using SSR we have to load layout data
    if (!this.props.isSSR) {
      this.updateLayoutData();
    }
  }

  extractRouteData = () => {
    if (!this.props.sitecoreContext) return null;

    const { route, ...context } = this.props.sitecoreContext;

    return {
      sitecore: {
        route,
        context
      }
    };
  };

  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */
  updateLayoutData() {
    const language = this.getLanguage();
    const route = this.props.route;

    this.setState(
      {
        loading: true
      },
      () => {
        // get the route data for the new route
        getRouteData(route, language).then((routeData) => {
          if (routeData) {
            if (routeData.sitecore && routeData.sitecore.route) {

              // set the sitecore context data and push the new route
              if (this.props.updateSitecoreContext) {
                this.props.updateSitecoreContext({
                  route: routeData.sitecore.route,
                  itemId: routeData.sitecore.route.itemId,
                  ...routeData.sitecore.context
                });
              }
              this.setState({ notFound: false, loading: false });
            } else {
              // A missing route value signifies an invalid path, so set notFound.
              const statusCode = 404;

              getRouteData(`/404`, language).then((routeData) => {

                this.setState({ notFound: false, loading: false }, () => {
                  if (this.props.updateSitecoreContext) {
                    this.props.updateSitecoreContext({
                      route: routeData.sitecore.route,
                      itemId: routeData.sitecore.route.itemId,
                      ...routeData.sitecore.context
                    });
                  }
                });
              });

            }
          }
        });
      }
    );
  }

  getLanguage() {
    return this.props.language || this.props.sitecoreContext.language || config.defaultLanguage;
  }

  /**
   * Updates the current app language to match the route data.
   */
  updateLanguage() {
    const newLanguage = this.getLanguage();

    if (i18n.language !== newLanguage) {
      i18n.changeLanguage(newLanguage);
    }
  }

  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.url;
    const newRoute = this.props.url;

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute) {
      return;
    }

    // if in Sitecore editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isEditorActive()) {
      window.location.assign(newRoute);
      return;
    }

    this.updateLanguage();
    this.updateLayoutData();
  }

  render() {
    const { notFound, loading } = this.state;
    const routeData = this.extractRouteData();
    const layoutData = this.props.sitecoreContext;

    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and Sitecore intergrated rendering know how to send 404 status codes).
    // `route` is null in case if route is not found
    if (notFound && routeData) {
      return (
        <>
          <Helmet>
            <title>{i18n.t('Page not found')}</title>
          </Helmet>
          <h1>Page not found</h1>
        </>
      );
    }

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    if (!routeData) {
      return null;
    }

    // Don't render anything if loading state is true.
    if (loading) {
      return null;
    }

    // Get layout to render for route
    const Layout = LayoutFactory.resolveLayout(layoutData.route);

    if (!Layout) {
      return (
        <p>
          Could not find layout, layoutData.route:
          <br />
          <pre>{JSON.stringify(layoutData.route, null, 2)}</pre>
        </p>
      );
    }

    // Render the app's root structural layout
    return (
      <Layout
        context={layoutData}
        route={layoutData.route}
        loading={loading}
      />
    );
  }
}

export default withSitecoreContext({ updatable: true })(RouteHandler);


/**
 * Gets route data from Sitecore. This data is used to construct the component layout for a JSS route.
 * @param {string} route Route path to get data for (e.g. /about)
 * @param {string} language Language to get route data in (content language, e.g. 'en')
 */
function getRouteData(route, language) {
  const layoutServiceInstance = layoutServiceFactory.create();
  return layoutServiceInstance.fetchLayoutData(route, language).catch((error) => {
    if (error.response && error.response.status === 401) {
      // fetch and show /401 page
      return layoutServiceInstance.fetchLayoutData('/401', language);
    }
    console.error('Route data fetch error', error, error.response);
    return null;
  });
}